import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  ScatterChart, Scatter, ReferenceLine,
} from "recharts";
import { api } from "../lib/api";
import { fmtBRL, fmtPct, fmtNumber } from "../lib/format";
import { computeDelta, type DeltaResult } from "../lib/delta";
import { useFilters, periodToDates } from "../lib/filters";
import { KpiTile, SplitMeter } from "../components/intelligence/KpiTile";
import { CardMemoryTrigger } from "../components/card-memory/CardMemoryTrigger";
import { ChartCard } from "../components/ChartCard";
import { PageHeader } from "../components/PageHeader";
import { LoadMoreRow, useProgressiveList } from "../components/LoadMoreRow";
import { StatusPill } from "../components/StatusPill";
import { IconAlertTriangle, IconChevronRight } from "../components/icons";
import type { TransportMarginByPlateDto, MarginPlateRow } from "@bi/types";
import { SEM_EXTRATO_TITLE } from "../components/TollCoverageNotice";
import { QueryErrorBanner } from "../components/QueryErrorBanner";

const fmt2 = (v: number) => v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const COMP_COLOR: Record<string, string> = {
  "Receita Total": "var(--brand)", "Combustível": "#DAA72B", "Manutenção": "#E8A33D",
  "Motorista": "#8B5CF6", "Pedágio": "#14B8A6", "Despesas de Viagem": "#EC4899",
  "Seguro de Carga": "#3B82F6", "Margem Final": "var(--brand)",
};
/** Mesmo corte de margem usado no backend para o status da placa (12% bom, 6% atenção). */
const marginTone = (pct: number | null): "warning" | "critical" | null =>
  pct == null ? null : pct >= 0.12 ? null : pct >= 0.06 ? "warning" : "critical";

/** Fundo/borda das linhas de "Pontos de atenção" por severidade (mesmo padrão de Pneus). */
const SEV_BOX: Record<string, string> = {
  critical: "bg-danger-50 border-danger-100",
  warning: "bg-warning-50 border-warning-100",
  info: "bg-info-50 border-info-100",
};
const SEV_DOT: Record<string, string> = { critical: "bg-danger-500", warning: "bg-warning-500", info: "bg-info-500" };
const SEV_LABEL: Record<string, string> = { critical: "Crítico", warning: "Atenção", info: "Destaque" };
const SEV_TEXT: Record<string, string> = { critical: "text-danger-600", warning: "text-warning-700", info: "text-info-700" };

/**
 * Placa sem nenhum movimento no período (sem faturamento, custo nem km). A API
 * devolve TODAS as placas cadastradas (3.700+ na Transmasi, ~150 com movimento);
 * listar as vazias fazia o ranking dizer "3729+ placas · topo do ranking".
 */
const hasActivity = (p: MarginPlateRow) =>
  p.hasRevenue || p.directCost > 0 || p.km > 0;

export function MarginByPlatePage() {
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);

  const { data, isLoading, isError: dataError, refetch: dataRefetch } = useQuery({
    queryKey: ["transport", "margin", from, to, filters.comparison],
    queryFn: () =>
      api.get<TransportMarginByPlateDto>(
        `/dashboards/transport/margin?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&comparison=${filters.comparison}`,
      ),
    staleTime: 60_000,
  });

  const s = data?.summary;
  const prev = data?.prevSummary;
  const cmp = data?.comparisonLabel ?? "vs. período anterior";
  const composition = data?.composition ?? [];
  const allPlates = data?.byPlate ?? [];
  const worst = data?.worstMargin ?? [];
  const worstConsumption = data?.worstConsumption ?? [];
  const attention = data?.attention ?? [];

  const revDelta    = computeDelta(s?.totalRevenue, prev?.totalRevenue, { direction: "higher_is_better" });
  const marginDelta = computeDelta(s?.totalMargin, prev?.totalMargin, { direction: "higher_is_better" });
  // minBase bem menor que o padrão (100): receita/km é um indicador de escala
  // pequena (ex. R$ 15,00) — com o padrão, o guard de "base pequena demais"
  // disparava sempre e o delta nunca aparecia, mesmo havendo variação real.
  const rpkDelta    = computeDelta(s?.revenuePerKm ?? null, prev?.revenuePerKm ?? null, { direction: "higher_is_better", minBase: 1 });
  // Margem média varia em pontos percentuais (p.p.), não em % do próprio valor.
  const ppDelta: DeltaResult | null = (s?.avgMarginPct != null && prev?.avgMarginPct != null)
    ? (() => {
        const pp = (s.avgMarginPct - prev.avgMarginPct) * 100;
        const flat = Math.abs(pp) < 0.05;
        return {
          pct: null,
          label: `${fmt2(Math.abs(pp)).replace(/,?0+$/, "") || "0"} p.p.`,
          trend: flat ? "flat" : pp > 0 ? "up" : "down",
          isGood: flat ? null : pp > 0,
        };
      })()
    : null;

  const byPlate = useMemo(() => allPlates.filter(hasActivity), [allPlates]);
  const hiddenPlates = allPlates.length - byPlate.length;
  // A coluna Seguro só aparece quando alguma placa tem valor: hoje nenhuma
  // apólice está cadastrada e a coluna era 100% "R$ 0,00".
  const showSeguro = byPlate.some((p) => p.seguro > 0);
  const tableCols = showSeguro ? 14 : 13;
  const maxRevenue = Math.max(1, ...byPlate.map((p) => p.revenue));

  const fleetRpk = s?.revenuePerKm ?? 0;
  const plateList = useProgressiveList(byPlate);
  const top10 = byPlate.filter((p) => p.margin > 0).slice(0, 10).map((p) => ({ plate: p.plate, margin: p.margin }));
  // km mínimo igual ao usado na Visão Geral (mesmo critério do backend pros
  // alertas de custo/km, `FLEET_COST_PER_KM_MIN_KM`): com km muito baixo o
  // denominador não representa uso real — um veículo com 4km rodados e
  // revenuePerKm/costPerKm na casa dos milhares distorcia a escala inteira,
  // esmagando todos os outros pontos perto do zero.
  const SCATTER_MIN_KM = 1000;
  const scatter = byPlate.filter((p) => p.km >= SCATTER_MIN_KM && p.revenuePerKm != null && p.costPerKm != null)
    .map((p) => ({ x: p.costPerKm as number, y: p.revenuePerKm as number, plate: p.plate,
      above: (p.marginPct ?? -1) >= (s?.avgMarginPct ?? 0) }));
  const scatterAbove = scatter.filter((d) => d.above);
  const scatterBelow = scatter.filter((d) => !d.above);

  const costItems = composition.filter((c) => c.kind === "cost");
  const revenueItem = composition.find((c) => c.kind === "revenue");
  const marginItem = composition.find((c) => c.kind === "margin");
  const fleetKmL = s?.fleetKmPerLiter ?? null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Frota · Margem por Placa"
        subtitle="Rentabilidade por veículo com faturamento, custos diretos e margem operacional"
      />

      {dataError && <QueryErrorBanner onRetry={() => { dataRefetch(); }} />}

      {/* KPIs. Selo só quando a margem pede atenção (abaixo de 12%), não "Bom"
          sempre ligado, mesmo critério das outras telas de Frota. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <CardMemoryTrigger memory={{ cardKey: "margem.faturamento", from, to, expected: s?.totalRevenue, title: "Faturamento Total" }}>
          <KpiTile label="Faturamento total" value={s ? fmtBRL(s.totalRevenue) : "—"}
            delta={revDelta} deltaLabel={cmp} loading={isLoading} />
        </CardMemoryTrigger>
        <CardMemoryTrigger memory={{ cardKey: "margem.custo-direto", from, to, expected: s?.totalDirectCost, title: "Custo Direto Total" }}>
          <KpiTile label="Custo direto total" value={s ? fmtBRL(s.totalDirectCost) : "—"}
            footnote="Combustível, manutenção, motorista, pedágio, despesas e seguro" loading={isLoading}
            visual={s && s.totalRevenue > 0 ? (
              <SplitMeter parts={[
                { label: "Custo", value: s.totalDirectCost, display: fmtPct((s.totalDirectCost / s.totalRevenue) * 100), tone: "danger" },
                { label: "Margem", value: Math.max(0, s.totalMargin), display: fmtPct((Math.max(0, s.totalMargin) / s.totalRevenue) * 100), tone: "brand" },
              ]} />
            ) : undefined} />
        </CardMemoryTrigger>
        <CardMemoryTrigger memory={{ cardKey: "margem.margem", from, to, expected: s?.totalMargin, title: "Margem Operacional" }}>
          <KpiTile label="Margem operacional" value={s ? fmtBRL(s.totalMargin) : "—"}
            delta={marginDelta} deltaLabel={cmp}
            status={s ? marginTone(s.avgMarginPct) : null} loading={isLoading} />
        </CardMemoryTrigger>
        <CardMemoryTrigger memory={{ cardKey: "margem.margem-media", from, to, expected: s?.avgMarginPct == null ? null : s.avgMarginPct * 100, title: "Margem Média" }}>
          <KpiTile label="Margem média" value={s?.avgMarginPct != null ? fmtPct(s.avgMarginPct * 100) : "—"}
            delta={ppDelta} deltaLabel={cmp}
            status={s ? marginTone(s.avgMarginPct) : null} loading={isLoading} />
        </CardMemoryTrigger>
        <CardMemoryTrigger memory={{ cardKey: "margem.receita-km", from, to, expected: s?.revenuePerKm, title: "Receita por KM" }}>
          <KpiTile label="Receita por km" value={s?.revenuePerKm != null ? `R$ ${fmt2(s.revenuePerKm)}` : "—"}
            delta={rpkDelta} deltaLabel={cmp} loading={isLoading} />
        </CardMemoryTrigger>
        <CardMemoryTrigger memory={{ cardKey: "margem.placa-mais-rentavel", from, to, expected: s?.mostProfitablePlate ? s.mostProfitableMargin : undefined, title: "Placa Mais Rentável" }}>
          <KpiTile label="Placa mais rentável" value={s?.mostProfitablePlate ?? "—"}
            footnote={s?.mostProfitablePlate ? `${fmtBRL(s.mostProfitableMargin)} de margem operacional` : undefined}
            loading={isLoading} />
        </CardMemoryTrigger>
      </div>

      {/* Pontos de atenção + Top 10 + scatter */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card overflow-hidden flex flex-col">
          <div className="px-5 py-4 border-b border-surface-border">
            <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <IconAlertTriangle className="w-4 h-4 text-amber-500" />
              Pontos de atenção da rentabilidade
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              {attention.length > 0 ? `${attention.length} no período · clique para abrir o Raio-X da placa` : "Margem, custo/km e consumo fora do padrão"}
            </p>
          </div>
          <div className="p-4 space-y-2 flex-1">
            {isLoading ? <div className="h-40 bg-gray-50 rounded-lg animate-pulse" />
              : attention.length === 0 ? <p className="text-sm text-gray-400">Sem pontos de atenção no período.</p>
              : attention.map((a, i) => {
                const body = (
                  <>
                    <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${SEV_DOT[a.severity]}`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-[10px] font-semibold uppercase tracking-wide ${SEV_TEXT[a.severity]}`}>{SEV_LABEL[a.severity]}</p>
                      <p className="text-sm text-gray-800 leading-snug">{a.title}</p>
                    </div>
                    {a.plate && <IconChevronRight className="w-3.5 h-3.5 text-gray-400 mt-1 flex-shrink-0" />}
                  </>
                );
                const cls = `flex items-start gap-2.5 rounded-lg border px-3 py-2 ${SEV_BOX[a.severity]}`;
                return a.plate
                  ? <Link key={i} to={`/frota/placa/${encodeURIComponent(a.plate)}`} className={`${cls} hover:brightness-[0.98]`}>{body}</Link>
                  : <div key={i} className={cls}>{body}</div>;
              })}
          </div>
        </div>

        <ChartCard title="Top 10 placas por margem" subtitle="Por margem operacional (R$)" height={260}
          isEmpty={!isLoading && top10.length === 0} loading={isLoading}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={top10} layout="vertical" margin={{ top: 0, right: 72, left: 10, bottom: 0 }}>
              <defs>
                <linearGradient id="grad-margin-top10" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="var(--brand)" stopOpacity={0.55} />
                  <stop offset="100%" stopColor="var(--brand)" stopOpacity={0.95} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
              <XAxis type="number" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <YAxis type="category" dataKey="plate" tick={{ fontSize: 11, fill: "#6b7280" }} tickLine={false} axisLine={false} width={72} />
              <Tooltip formatter={(v: number) => [fmtBRL(v), "Margem"]} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }} />
              <Bar dataKey="margin" fill="url(#grad-margin-top10)" radius={[0, 4, 4, 0]}
                label={{ position: "right", formatter: (v: number) => fmtBRL(v), fontSize: 10, fill: "#6b7280" }} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Receita × Custo por KM" subtitle="Cada ponto representa um veículo com pelo menos 1.000 km no período" height={260}
          isEmpty={!isLoading && scatter.length === 0} loading={isLoading}>
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              {/* \u00A0 (espaço não-quebrável) em vez de espaço normal entre "R$" e o número: espaço
                  comum faz o Recharts quebrar linha no rótulo do tick. */}
              <XAxis type="number" dataKey="x" name="Custo/km" tickFormatter={(v) => `R$ ${fmt2(v)}`}
                tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <YAxis type="number" dataKey="y" name="Receita/km" tickFormatter={(v) => `R$ ${fmt2(v)}`}
                tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} width={64} />
              {fleetRpk > 0 && <ReferenceLine y={fleetRpk} stroke="#9ca3af" strokeDasharray="6 4"
                label={{ value: "Média da frota", position: "insideTopRight", fontSize: 10, fill: "#9ca3af" }} />}
              <Tooltip cursor={{ strokeDasharray: "3 3" }}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }}
                formatter={(v: number, n: string) => [`R$ ${fmt2(v)}`, n]} labelFormatter={() => ""} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Scatter name="Margem acima da média" data={scatterAbove} fill="var(--brand)" />
              <Scatter name="Margem abaixo da média" data={scatterBelow} fill="#E5484D" />
            </ScatterChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Composição da margem + piores margens + piores consumos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Sem altura fixa: no ChartCard de 300px a última linha ("Margem
            Final", a mais importante) ficava cortada fora do card. */}
        <div className="card overflow-hidden flex flex-col">
          <div className="px-5 py-4 border-b border-surface-border">
            <p className="text-sm font-semibold text-gray-900">Da receita à margem</p>
            <p className="text-xs text-gray-400 mt-0.5">Quanto cada custo direto consome do faturamento</p>
          </div>
          <div className="p-5 flex-1">
            {isLoading ? <div className="h-48 bg-gray-50 rounded-lg animate-pulse" />
              : !revenueItem || composition.every((c) => c.value === 0) ? <p className="text-sm text-gray-400">Sem dados no período.</p>
              : (
                <div className="space-y-3">
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="font-semibold text-gray-900">{revenueItem.label}</span>
                    <span className="font-semibold text-gray-900">{fmtBRL(revenueItem.value)}</span>
                  </div>
                  {costItems.map((c) => (
                    <div key={c.label}>
                      <div className="flex items-baseline gap-2 text-sm">
                        <span className={c.value === 0 ? "text-gray-400" : "text-gray-600"}>− {c.label}</span>
                        <span className={`ml-auto whitespace-nowrap ${c.value === 0 ? "text-gray-400" : "text-gray-800"}`}>
                          {c.value === 0 ? "sem valor no período" : fmtBRL(c.value)}
                        </span>
                        <span className="w-12 text-right text-xs text-gray-400">{c.value === 0 ? "" : fmtPct(c.pct * 100)}</span>
                      </div>
                      {c.value > 0 && (
                        <div className="mt-1 h-1.5 rounded-full bg-surface-muted overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${Math.min(100, c.pct * 100)}%`, background: COMP_COLOR[c.label] ?? "#9CA3AF" }} />
                        </div>
                      )}
                    </div>
                  ))}
                  {marginItem && (
                    <div className={`mt-1 rounded-lg px-3 py-2.5 ${marginItem.value >= 0 ? "bg-tm-50" : "bg-danger-50"}`}>
                      <div className="flex items-baseline gap-2">
                        <span className={`text-sm font-semibold ${marginItem.value >= 0 ? "text-tm-800" : "text-danger-700"}`}>= {marginItem.label}</span>
                        <span className={`ml-auto text-base font-bold whitespace-nowrap ${marginItem.value >= 0 ? "text-tm-800" : "text-danger-700"}`}>{fmtBRL(marginItem.value)}</span>
                        <span className={`w-12 text-right text-xs font-semibold ${marginItem.value >= 0 ? "text-tm-700" : "text-danger-600"}`}>{fmtPct(marginItem.pct * 100)}</span>
                      </div>
                      <div className="mt-1.5 h-2 rounded-full bg-white/70 overflow-hidden">
                        <div className={`h-full rounded-full ${marginItem.value >= 0 ? "bg-tm-500" : "bg-danger-500"}`} style={{ width: `${Math.min(100, Math.max(0, marginItem.pct * 100))}%` }} />
                      </div>
                    </div>
                  )}
                </div>
              )}
          </div>
        </div>

        <div className="card overflow-hidden flex flex-col">
          <div className="px-5 py-4 border-b border-surface-border">
            <p className="text-sm font-semibold text-gray-900">Veículos com pior margem</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Margem operacional (%){s?.avgMarginPct != null ? ` · média da frota ${fmtPct(s.avgMarginPct * 100)}` : ""}
            </p>
          </div>
          <div className="flex-1">
            {isLoading ? <p className="px-5 py-6 text-sm text-center text-gray-400">Carregando…</p>
              : worst.length === 0 ? <p className="px-5 py-6 text-sm text-center text-gray-400">Sem dados no período.</p>
              : (
                <ul className="divide-y divide-surface-border">
                  {worst.map((w, i) => (
                    <li key={w.plate}>
                      <Link to={`/frota/placa/${encodeURIComponent(w.plate)}`} className="flex items-center gap-3 px-5 py-2.5 hover:bg-gray-50">
                        <span className="text-xs font-bold text-gray-400 w-4">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-tm-700">{w.plate}</p>
                          <p className="text-xs text-gray-400 truncate" title={w.driver ?? undefined}>{w.driver ?? "Sem motorista no período"}</p>
                        </div>
                        <span className={`text-sm font-semibold tabular-nums ${w.marginPct != null && w.marginPct < 0 ? "text-danger-600" : "text-gray-800"}`}>
                          {w.marginPct != null ? fmtPct(w.marginPct * 100) : "—"}
                        </span>
                        <StatusPill status={w.status} />
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
          </div>
        </div>

        <div className="card overflow-hidden flex flex-col">
          <div className="px-5 py-4 border-b border-surface-border">
            <p className="text-sm font-semibold text-gray-900">Veículos com pior consumo</p>
            <p className="text-xs text-gray-400 mt-0.5">
              km/l diesel{fleetKmL != null ? ` · média da frota ${fmt2(fleetKmL)} km/l` : ""} · mínimo 3 abastecimentos e 500 km
            </p>
          </div>
          <div className="flex-1">
            {isLoading ? <p className="px-5 py-6 text-sm text-center text-gray-400">Carregando…</p>
              : worstConsumption.length === 0 ? <p className="px-5 py-6 text-sm text-center text-gray-400">Sem placas com base mínima de abastecimentos no período.</p>
              : (
                <ul className="divide-y divide-surface-border">
                  {worstConsumption.map((w, i) => (
                    <li key={w.plate}>
                      <Link to={`/frota/placa/${encodeURIComponent(w.plate)}`} className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50">
                        <span className="text-xs font-bold text-gray-400 w-4">{i + 1}</span>
                        <span className="w-20 text-sm font-medium text-tm-700">{w.plate}</span>
                        {/* Barra: km/l da placa contra a média da frota (marca vertical). */}
                        <div className="flex-1 relative h-1.5 rounded-full bg-surface-muted">
                          <div className="absolute inset-y-0 left-0 rounded-full bg-danger-400"
                            style={{ width: `${fleetKmL ? Math.min(100, (w.kmPerLiter / fleetKmL) * 80) : 0}%` }} />
                          {fleetKmL != null && <span className="absolute -top-1 bottom-[-4px] w-px bg-gray-400" style={{ left: "80%" }} title="Média da frota" />}
                        </div>
                        <span className="w-12 text-right text-sm font-semibold text-gray-800 tabular-nums">{fmt2(w.kmPerLiter)}</span>
                        <span className={`w-16 text-right text-xs font-semibold ${w.vsFleetPct != null && w.vsFleetPct < 0 ? "text-danger-600" : "text-tm-700"}`}>
                          {w.vsFleetPct != null ? `${w.vsFleetPct >= 0 ? "+" : ""}${fmt2(w.vsFleetPct * 100)}%` : "—"}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
          </div>
          {worstConsumption.length > 0 && fleetKmL != null && (
            <p className="px-5 py-2.5 text-[11px] text-gray-400 border-t border-surface-border">O traço vertical marca a média da frota.</p>
          )}
        </div>
      </div>

      {/* Ranking detalhado por placa */}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-surface-border">
          <p className="text-sm font-semibold text-gray-900">Ranking detalhado por placa</p>
          <p className="text-xs text-gray-400 mt-0.5">
            Faturamento, custos diretos e margem operacional por veículo
            {data ? ` · ${fmtNumber(byPlate.length)} placas com movimento no período` : ""}
            {hiddenPlates > 0 ? ` (${fmtNumber(hiddenPlates)} cadastradas sem movimento não aparecem)` : ""}
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-400 border-b border-surface-border">
                <th className="px-5 py-2 text-left font-medium">Placa</th>
                <th className="px-3 py-2 text-left font-medium">Motorista</th>
                <th className="px-3 py-2 text-right font-medium">Faturamento</th>
                <th className="px-3 py-2 text-right font-medium">Combustível</th>
                <th className="px-3 py-2 text-right font-medium">Manutenção</th>
                <th className="px-3 py-2 text-right font-medium">Salário Motorista</th>
                <th className="px-3 py-2 text-right font-medium">Pedágio</th>
                {showSeguro && <th className="px-3 py-2 text-right font-medium">Seguro</th>}
                <th className="px-3 py-2 text-right font-medium">Margem R$</th>
                <th className="px-3 py-2 text-right font-medium">Margem %</th>
                <th className="px-3 py-2 text-right font-medium">Receita/KM</th>
                <th className="px-3 py-2 text-right font-medium">Custo/KM</th>
                <th className="px-3 py-2 text-right font-medium">km/l</th>
                <th className="px-5 py-2 text-center font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={tableCols} className="px-5 py-6 text-center text-gray-400">Carregando…</td></tr>
              ) : byPlate.length === 0 ? (
                <tr><td colSpan={tableCols} className="px-5 py-6 text-center text-gray-400">Sem dados no período.</td></tr>
              ) : plateList.visible.map((p) => (
                <tr key={p.plate} className="border-b border-surface-border/60 hover:bg-gray-50">
                  <td className="px-5 py-2.5 font-medium">
                    <Link to={`/frota/placa/${encodeURIComponent(p.plate)}`} className="text-tm-700 hover:underline">{p.plate}</Link>
                    {p.trailers.length > 0 && (
                      <p className="text-xs font-normal text-blue-600" title="Custo da carreta somado nesta linha (vínculo Sinersoft)">
                        + carreta {p.trailers.join(" · ")}
                      </p>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-gray-600 max-w-[200px] truncate" title={p.driver ?? undefined}>{p.driver ?? "—"}</td>
                  <td className="px-3 py-2.5 text-right text-gray-700">
                    {p.hasRevenue ? (
                      <>
                        {p.revenueFormatted}
                        <div className="mt-1 ml-auto h-1 w-20 rounded-full bg-surface-muted overflow-hidden">
                          <div className="h-full rounded-full bg-tm-400" style={{ width: `${(p.revenue / maxRevenue) * 100}%` }} />
                        </div>
                      </>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right text-gray-600">{fmtBRL(p.fuel)}</td>
                  <td className="px-3 py-2.5 text-right text-gray-600">{fmtBRL(p.maintenance)}</td>
                  <td className="px-3 py-2.5 text-right text-gray-600">{fmtBRL(p.driverPay + p.commission)}</td>
                  <td className="px-3 py-2.5 text-right text-gray-600">
                    {p.tollsSource === "sem_extrato"
                      ? <span className="text-xs text-gray-400" title={SEM_EXTRATO_TITLE}>sem extrato</span>
                      : p.tollsSource == null ? <span className="text-gray-300">—</span> : fmtBRL(p.tolls)}
                  </td>
                  {showSeguro && <td className="px-3 py-2.5 text-right text-gray-600">{fmtBRL(p.seguro)}</td>}
                  <td className={`px-3 py-2.5 text-right font-semibold ${p.margin >= 0 ? "text-tm-700" : "text-danger-600"}`}>{p.marginFormatted}</td>
                  <td className={`px-3 py-2.5 text-right ${p.marginPct != null && p.marginPct < 0 ? "text-danger-600 font-medium" : "text-gray-700"}`}>{p.marginPct != null ? fmtPct(p.marginPct * 100) : "—"}</td>
                  <td className="px-3 py-2.5 text-right text-gray-600">{p.revenuePerKm != null ? `R$ ${fmt2(p.revenuePerKm)}` : "—"}</td>
                  <td className="px-3 py-2.5 text-right text-gray-600">{p.costPerKm != null ? `R$ ${fmt2(p.costPerKm)}` : "—"}</td>
                  <td className="px-3 py-2.5 text-right text-gray-600">{p.kmPerLiter != null ? fmt2(p.kmPerLiter) : "—"}</td>
                  <td className="px-5 py-2.5 text-center"><StatusPill status={p.status} /></td>
                </tr>
              ))}
              {/* capped=false: a lista já vem inteira (não é o teto da API). */}
              {!isLoading && <LoadMoreRow list={plateList} colSpan={tableCols} label="placas" capped={false} />}
            </tbody>
          </table>
        </div>
        <p className="px-5 py-3 text-xs text-gray-400 border-t border-surface-border">
          Margem <strong>parcial</strong>: inclui combustível, manutenção, motorista, pedágio, despesas de viagem e seguro.
          Não inclui pneus, IPVA/licenciamento, financiamento e custos administrativos. km/l considera apenas diesel.
          Motorista e seguro vêm das VIAGENS do período (controle de viagens). Placa sem viagem no período aparece
          sem esses custos e o seguro cai na exposição estimada.
          {!showSeguro && " Nenhuma placa tem seguro de carga no período, por isso a coluna Seguro não aparece."}
          {" "}<strong>Pedágio</strong>: só o PAGO na praça (extrato Sem Parar importado em Frota › Pedágio, passagens
          datadas). Período sem extrato aparece como "sem extrato" e não entra no custo.
          {s && s.tollsFromInvoices > 0 && (
            <> Neste período: {fmtBRL(s.tollsFromInvoices)} de extrato em {s.platesWithPaidTolls} placa(s).</>
          )}
          {" "}Fonte: Sinersoft (CT-e status 2, abastecimentos, ordens de serviço, viagens). "—" = sem dado no período, não é zero.
        </p>
      </div>
    </div>
  );
}
